'use client'

import { useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { images } from '../../constants'
import { useTranslation } from 'react-i18next'
import LanguageSelector from '../../LanguageSelector/LanguageSelector'
import { useAuth } from '../../contexts/authContext'

export default function Header() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isAuthenticated } = useAuth()

  const navigation = [
    { name: t("headerHome"), href: '/' },
    { name: t("headerAbout"), href: '/#about' },
    { name: t("headerPricing"), href: '/#pricing' },
    { name: t("headerFaq"), href: '/#faq' },
    { name: t("headerGuide"), href: '/pages/guide' },
  ]

  return (
    <header className="relative z-50">
      <nav
        aria-label="Global"
        className="mx-auto flex max-w-[1280px] items-center justify-between gap-x-4 px-6 py-5 lg:px-16"
      >
        {/* Logo */}
        <div className="flex flex-1 lg:flex-none">
          <a href="/" className="-m-1.5 p-1.5">
            <span className="sr-only">Klassekartgenerator</span>
            <img alt="" src={images[`${t("logo")}`]} className="h-11 w-auto" />
          </a>
        </div>

        {/* Center pill nav */}
        <div className="pointer-events-none absolute left-1/2 hidden -translate-x-1/2 lg:block">
          <div className="pointer-events-auto flex items-center gap-1 rounded-full bg-surface p-1.5 shadow-subtle">
            {navigation.map((item, i) => (
              <a
                key={item.name}
                href={item.href}
                className={
                  i === 0
                    ? 'rounded-full bg-ink px-[18px] py-[9px] text-sm font-semibold text-white'
                    : 'rounded-full px-[18px] py-[9px] text-sm font-medium text-muted transition-colors hover:bg-tint hover:text-ink'
                }
              >
                {item.name}
              </a>
            ))}
          </div>
        </div>

        {/* Right actions */}
        <div className="flex items-center justify-end gap-x-3">
          <div className="hidden lg:block">
            <LanguageSelector />
          </div>
          {!isAuthenticated && (
            <a
              href={t("loginLink")}
              className="hidden text-sm font-medium text-muted transition-colors hover:text-ink lg:block"
            >
              {t("headerLogin")}
            </a>
          )}
          <a
            href={isAuthenticated ? t("loginLink") : t("signupLink")}
            className="ds-cta hidden h-11 px-6 text-sm lg:inline-flex"
          >
            {isAuthenticated ? t("goToApp") : t("headerSignup")}
          </a>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-ink lg:hidden"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-50" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-cream px-6 py-6 sm:max-w-sm sm:shadow-float">
          <div className="flex items-center justify-between gap-x-6">
            <a href="/" className="-m-1.5 p-1.5" onClick={() => setMobileMenuOpen(false)}>
              <span className="sr-only">Klassekartgenerator</span>
              <img alt="" src={images[`${t("logo")}`]} className="h-10 w-auto" />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-ink"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="mt-8 flow-root">
            <div className="-my-6 divide-y divide-border_soft">
              <div className="space-y-1 py-6">
                {navigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="-mx-3 block rounded-xl px-3 py-2.5 text-base font-semibold text-ink hover:bg-tint"
                  >
                    {item.name}
                  </a>
                ))}
              </div>
              <div className="space-y-3 py-6">
                <a
                  href={isAuthenticated ? t("loginLink") : t("signupLink")}
                  onClick={() => setMobileMenuOpen(false)}
                  className="ds-cta h-12 w-full text-base"
                >
                  {isAuthenticated ? t("goToApp") : t("headerSignup")}
                </a>
                {!isAuthenticated && (
                  <a
                    href={t("loginLink")}
                    onClick={() => setMobileMenuOpen(false)}
                    className="-mx-3 block rounded-xl px-3 py-2.5 text-base font-semibold text-ink hover:bg-tint"
                  >
                    {t("headerLogin")}
                  </a>
                )}
              </div>
              <div className="py-6">
                <LanguageSelector />
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
}
